/**
 * AmountWithBreakdown.jsx
 *
 * Renders a monetary total with a small status dot and a hover tooltip
 * showing the per-`paid_by_name` (or `refund_to`) breakdown. Used in the
 * Paid and Refund columns of PF Bills, All Bills, and Bill Details.
 *
 *   • Green dot  → exactly one transaction makes up the total
 *   • Red dot    → multiple transactions
 *   • No dot     → no transactions (amount is 0 or breakdown empty)
 *
 * Props:
 *   amount     (number) — the total to display
 *   breakdown  (object) — e.g. { BillPortal: 100.50, Anchor: 50.00, Customer: 0 }
 *   count      (number) — total transaction count (drives the dot color)
 *   label      (string) — "Paid" or "Refund" (used in tooltip header)
 *   testId     (string)
 *   amountClassName (string) — extra classes for the amount text
 */
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const fmtC = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '$0.00';
  return '$' + Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

export default function AmountWithBreakdown({
  amount,
  breakdown = {},
  count = 0,
  label = 'Paid',
  testId = 'amount-breakdown',
  amountClassName = '',
}) {
  const total = Number(amount || 0);
  const entries = Object.entries(breakdown || {}).filter(
    ([, v]) => Number(v || 0) > 0.001
  );

  const hasDot = total > 0.001 && count > 0;
  const dotColor =
    count >= 2 ? 'bg-red-500' : count === 1 ? 'bg-emerald-500' : 'bg-muted';

  const dot = hasDot ? (
    <span
      className={`inline-block h-1.5 w-1.5 rounded-full ${dotColor}`}
      data-testid={`${testId}-dot`}
      aria-label={count >= 2 ? `${count} transactions` : '1 transaction'}
    />
  ) : null;

  const amountEl = (
    <span className={`inline-flex items-center gap-1.5 ${amountClassName}`}>
      <span className="font-mono">{fmtC(total)}</span>
      {dot}
    </span>
  );

  // No breakdown — just render the plain amount without tooltip wrapper.
  if (!entries.length || total < 0.001) {
    return <span data-testid={testId}>{amountEl}</span>;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="cursor-help"
            data-testid={testId}
            data-count={count}
          >
            {amountEl}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="text-xs p-2.5 max-w-xs"
          data-testid={`${testId}-tooltip`}
        >
          <div className="font-semibold mb-1.5 flex items-center justify-between gap-3">
            <span>{label} breakdown</span>
            <span className="text-[10px] text-muted-foreground">
              {count} txn{count === 1 ? '' : 's'}
            </span>
          </div>
          <div className="space-y-0.5">
            {entries.map(([key, v]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-4 text-[11px]"
                data-testid={`${testId}-row-${String(key).toLowerCase()}`}
              >
                <span className="text-muted-foreground">{key}</span>
                <span className="font-mono font-semibold">{fmtC(v)}</span>
              </div>
            ))}
            <div className="border-t border-border/40 pt-1 mt-1 flex items-center justify-between gap-4 text-[11px] font-semibold">
              <span>Total</span>
              <span className="font-mono">{fmtC(total)}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
